import { useNavigate } from 'react-router-dom'

/**
 * The Claims / Evidence lens switch — the row under the crumb on S1, S2 and S3.
 *
 * The two lenses are two QUESTIONS over one universe, not two altitudes — a
 * claim and every document that supports it, or a document and every claim it
 * supports. Neither contains the other, which is why the switch sits here and
 * not on the focus rail: the rail is for genuine zoom (invention → my
 * inventions → portfolio), and putting a lens on it would say the wrong thing.
 *
 * The Evidence side is a NAVIGATION, not a query-string flip: there is no
 * `?lens=evidence`, because the Evidence lens IS the document page. When the
 * invention has no document to open, the control is rendered disabled with the
 * honest reason rather than hidden — a lens that silently disappears reads as a
 * missing feature.
 */
export interface LensSwitchProps {
  /** Which lens the user is currently in. */
  lens: 'claims' | 'evidence'
  /** Switch to the Claims lens (the page owns the ?lens=claims search param). */
  onClaims: () => void
  /** Route of the document that opens the Evidence lens, or null when none exists. */
  evidenceHref: string | null
}

const PILL = 'rounded-full px-3 py-1 text-[12px] font-semibold transition-colors'
const ON = 'bg-iflow-600 text-white'
const OFF = 'border border-sky-200 bg-white text-gray-600 hover:text-iflow-700'

export default function LensSwitch({ lens, onClaims, evidenceHref }: LensSwitchProps) {
  const navigate = useNavigate()
  return (
    <div data-part="LensSwitch" className="mb-2.5 flex flex-wrap items-center gap-2">
      <span className="text-[12px] font-bold text-gray-500">Lens</span>
      <button
        type="button"
        onClick={onClaims}
        aria-pressed={lens === 'claims'}
        className={`${PILL} ${lens === 'claims' ? ON : OFF}`}
      >
        Claims
      </button>
      <button
        type="button"
        onClick={() => evidenceHref && navigate(evidenceHref)}
        disabled={!evidenceHref}
        aria-pressed={lens === 'evidence'}
        title={evidenceHref ? undefined : 'No document to open yet'}
        className={`${PILL} ${lens === 'evidence' ? ON : OFF} ${evidenceHref || lens === 'evidence' ? '' : 'cursor-not-allowed opacity-50'}`}
      >
        Evidence
      </button>
      <span className="text-[12px] text-gray-500">
        two questions over the same universe — neither is inside the other
      </span>
      {!evidenceHref && lens !== 'evidence' && (
        <span className="text-[11px] text-gray-500">No document to open yet</span>
      )}
    </div>
  )
}
